<template>
<div class="page-container">
	<div class="page-content">
		<!-- Loading Overlay -->
		<div
			v-if="loading"
			class="overlay-modal d-flex align-items-center justify-content-center"
		>
			<div>
				<div class="d-flex justify-content-center mb-2">
					<b-spinner label="Loading..."></b-spinner>
				</div>
				<div class="d-flex align-items-center">
					<strong>Carregando permissões</strong>
				</div>
			</div>
		</div>

		<div class="page-header">
			<div class="d-flex align-items-center mb-3">
				<h1 class="text-white"><i class="mdi mdi-lock"></i> Permissões por perfil</h1>

				<button @click="openNewPermissionModal" class="btn btn-outline-white ml-auto mr-2"><i class="fa fa-plus-circle"></i><span class="d-none d-md-inline"> Inserir nova permissão</span></button>

			</div>
		</div>

		<div class="page-body">
			<div class="card form">
				<div class="card-body">

					<div class="row mb-4">
						<div class="col-12 col-lg-12">
							<select v-model="selectedProfile" @change="loadPermissionsByProfile" class="custom-select font-18 w-auto shadow" name="perfil" id="perfil">
								<option value="">Selecione um perfil</option>
								<option v-for="role in roles" :key="role.id" :value="role.id">
									{{ role.name }}
								</option>
							</select>
							<p class="helper arrow-left">Selecione o perfil desejado para visualizar a grade de permissões</p>
						</div>
					</div>

					<div class="row">
						<div class="col-12 mb-3">
							<h5>
								<div class="form-subheader"><span>Permissões relacionadas</span></div>
							</h5>
						</div>
						<div class="col-12 col-lg-12">

							<PermissionsTable
								:permission-modules="permissionModules"
								:permission-actions="permissionActions"
								:selected-permissions="selectedPermissions"
								:permissions-matrix="permissionsMatrix"
								@permissions-changed="updatePermissions"
							/>

						</div>
						<div class="col-12 text-right pt-3">
							<button type="button" class="button btn btn-link text-secondary" @click="cancelChanges"><i class="fa fa-times mr-1"></i> Cancelar</button>
							<button type="button" class="button btn btn-primary" @click="savePermissions" :disabled="loading"><i class="fa fa-check mr-1"></i> Salvar permissões</button>
						</div>
					</div>
				</div>
			</div>
		</div>

	</div>

	<!-- Modal Nova Permissão -->
	<NewPermissionModal
	v-model="showNewPermissionModal"
	@save="handleNewPermission"
	/>
</div>
</template>

<script>
import NewPermissionModal from '@/components/Permissions/NewPermissionModal.vue';
import PermissionsTable from '@/components/Permissions/PermissionsTable.vue';
import permissionService from '@/services/permissions';

export default {
  name: 'PermissionsIndex',
  components: {
    NewPermissionModal,
    PermissionsTable
  },
  data() {
    return {
      loading: false,
      selectedProfile: null,
      showNewPermissionModal: false,
      roles: [],
      permissionModules: {},
      permissionActions: {},
      selectedPermissions: {},
      permissionsMatrix: {}
    };
  },
  mounted() {
    this.loadRoles();
  },
  methods: {
    
    async loadRoles() {
      try {
        const response = await permissionService.getRoles();
        if (response.data.success) {
          this.roles = response.data.data;
          // Set first role as default if available
          if (this.roles.length > 0) {
            this.selectedProfile = this.roles[0].id;
            this.loadPermissionsByProfile();
          }
        }
      } catch (error) {
        console.error('Erro ao carregar roles:', error);
        this.showErrorMessage('Erro ao carregar roles. Tente novamente.');
      }
    },
    
    async loadPermissionsByProfile() {
      if (!this.selectedProfile) return;
      
      this.loading = true;
      
      try {
        const response = await permissionService.getPermissionsMatrix(this.selectedProfile);
        
        if (response.data.success) {
          const matrix = response.data.data;
          
          // Store the full permissions matrix for passing to PermissionsTable
          this.permissionsMatrix = matrix;
          
          // Extract permission modules and actions from the matrix
          this.permissionModules = {};
          this.permissionActions = {};
          this.selectedPermissions = {};
          
          Object.keys(matrix).forEach(moduleSlug => {
            const moduleData = matrix[moduleSlug];
            
            // Set module data
            this.permissionModules[moduleSlug] = {
              name: moduleData.name,
              blacklist: moduleData.blacklist || []
            };
            
            // Initialize selected permissions for this module
            this.$set(this.selectedPermissions, moduleSlug, {});
            
            // Extract actions and set selected state
            const listPermission = moduleData.permissions['list'];
            const showPermission = moduleData.permissions['show'];
            
            // Create "acesso" from "list" + "show"
            if (listPermission || showPermission) {
              this.permissionActions['acesso'] = 'Acesso';
              this.$set(this.selectedPermissions[moduleSlug], 'acesso', 
                (listPermission && listPermission.selected) && (showPermission && showPermission.selected));
            }
            
            Object.keys(moduleData.permissions).forEach(actionSlug => {
              const actionData = moduleData.permissions[actionSlug];
              
              // Skip 'list' and 'show' actions as they are represented by 'acesso'
              if (actionSlug === 'list' || actionSlug === 'show') {
                return;
              }
              
              // Add action to permissionActions if not already added
              if (!this.permissionActions[actionSlug]) {
                this.permissionActions[actionSlug] = actionData.name;
              }
              
              // Set selected state
              this.$set(this.selectedPermissions[moduleSlug], actionSlug, actionData.selected || false);
            });
          });
        }
      } catch (error) {
        console.error('Erro ao carregar permissões do perfil:', error);
        this.showErrorMessage('Erro ao carregar permissões do perfil. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },
    
    updatePermissions(newPermissions) {
      this.selectedPermissions = newPermissions;
    },

    openNewPermissionModal() {
      this.showNewPermissionModal = true;
    },

    async handleNewPermission(roleData) {
      try {
        const response = await permissionService.createRole(roleData);
        
        if (response.data.success) {
          this.showSuccessMessage(`Role "${roleData.name}" criada com sucesso!`);
          // Reload roles list
          this.loadRoles();
        } else {
          this.showErrorMessage('Erro ao criar role.');
        }
      } catch (error) {
        console.error('Erro ao criar role:', error);
        this.showErrorMessage('Erro ao criar role. Tente novamente.');
      }
    },

    async savePermissions() {
      this.loading = true;
      
      try {
        // Transform "acesso" to "list" + "show" before sending to backend
        const transformedPermissions = this.transformPermissionsForBackend(this.selectedPermissions);
        
        const response = await permissionService.updatePermissions(this.selectedProfile, transformedPermissions);
        
        if (response.data.success) {
          const selectedRole = this.roles.find(role => role.id == this.selectedProfile);
          const roleName = selectedRole ? selectedRole.name : this.selectedProfile;
          
          this.showSuccessMessage(`Permissões do perfil "${roleName}" salvas com sucesso!`);
        } else {
          this.showErrorMessage('Erro ao salvar permissões.');
        }
      } catch (error) {
        console.error('Erro ao salvar permissões:', error);
        this.showErrorMessage('Erro ao salvar permissões. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    transformPermissionsForBackend(permissions) {
      const transformed = {};
      
      Object.keys(permissions).forEach(moduleSlug => {
        transformed[moduleSlug] = {};
        const modulePermissions = permissions[moduleSlug];
        
        Object.keys(modulePermissions).forEach(actionSlug => {
          if (actionSlug === 'acesso') {
            // Transform "acesso" to "list" + "show"
            transformed[moduleSlug]['list'] = modulePermissions[actionSlug];
            transformed[moduleSlug]['show'] = modulePermissions[actionSlug];
          } else {
            // Keep other permissions as they are
            transformed[moduleSlug][actionSlug] = modulePermissions[actionSlug];
          }
        });
      });
      
      return transformed;
    },

    cancelChanges() {
      this.loadPermissionsByProfile();
      this.showInfoMessage('Alterações canceladas.');
    },

    showSuccessMessage(message) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        });
      } else {
        alert(message);
      }
    },

    showErrorMessage(message) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Erro',
          variant: 'danger',
          solid: true
        });
      } else {
        alert(message);
      }
    },

    showInfoMessage(message) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Informação',
          variant: 'info',
          solid: true
        });
      } else {
        alert(message);
      }
    }
  }
};
</script>

<style scoped>


</style>